import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Play, Pause, DollarSign, Eye, MousePointer, TrendingUp,
  ChevronRight, ChevronLeft, Loader2, AlertTriangle, ArrowLeft, BarChart3
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Line, ComposedChart } from "recharts";

const fmt = (v) => v ? Number(v).toLocaleString() : "0";
const fmtMoney = (v) => v ? `$${(Number(v) / 1).toFixed(2)}` : "$0.00";
const fmtPct = (v) => v ? `${(Number(v) * 100).toFixed(2)}%` : "0%";

function StatusBadge({ status, budgetRemaining, hasBudget }) {
  // Only override status when the campaign/boost actually has its own budget.
  // CBO campaigns have no budget_remaining at campaign level — their ad sets hold the budget.
  let displayStatus = status;
  if (status === "ACTIVE" && hasBudget && budgetRemaining !== undefined && budgetRemaining !== null) {
    const remaining = Number(budgetRemaining);
    if (remaining <= 0) displayStatus = "COMPLETED";
    else if (remaining < 100) displayStatus = "ENDING";
  }
  const cls = displayStatus === "ACTIVE" ? "status-published"
    : displayStatus === "PAUSED" ? "status-pending"
    : displayStatus === "COMPLETED" ? "status-rejected"
    : displayStatus === "ENDING" ? "status-scheduled"
    : "status-unprocessed";
  return <span className={`status-badge ${cls}`}>{displayStatus.toLowerCase()}</span>;
}

function MetricCell({ label, value }) {
  return (
    <div className="text-center w-[88px]">
      <p className="text-[10px] text-zinc-500 uppercase font-mono tracking-wider">{label}</p>
      <p className="stat-number text-sm text-zinc-200 mt-0.5">{value}</p>
    </div>
  );
}

function ConfirmDialog({ open, onClose, onConfirm, title, description, loading }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-studio-raised border-[#2a2e36] rounded-2xl max-w-md" style={{ backgroundColor: "#1a1d21" }}>
        <DialogHeader>
          <DialogTitle className="text-zinc-100 font-display">{title}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-zinc-400">{description}</p>
        <DialogFooter className="gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-xl border border-[#363b45] text-zinc-400 text-sm">Cancel</button>
          <button onClick={onConfirm} disabled={loading} className="btn-gold px-4 py-2 rounded-xl text-sm flex items-center gap-2">
            {loading && <Loader2 size={14} className="animate-spin" />} Confirm
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CampaignRow({ camp, onDrill, onStatusChange, onBudgetChange }) {
  const ins = camp.insights || {};
  return (
    <div className="flex items-center gap-4 p-4 border-b border-[#222630] hover:bg-[#1a1d21] transition-colors cursor-pointer group"
      onClick={() => onDrill(camp.id)} data-testid={`campaign-${camp.id}`}>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-zinc-200 font-medium truncate">{camp.name}</p>
        <p className="text-xs text-zinc-500 mt-0.5">{camp.objective?.replace("OUTCOME_", "").replace(/_/g, " ")}</p>
      </div>
      <StatusBadge status={camp.effective_status || camp.status} budgetRemaining={camp.budget_remaining} hasBudget={!!(camp.daily_budget || camp.lifetime_budget)} />
      <div className="hidden lg:grid grid-cols-6 gap-0" style={{ width: "528px" }}>
        <MetricCell label="spend" value={fmtMoney(ins.spend)} />
        <MetricCell label="impr" value={fmt(ins.impressions)} />
        <MetricCell label="clicks" value={fmt(ins.clicks)} />
        <MetricCell label="ctr" value={fmtPct(ins.ctr)} />
        <MetricCell label="cpm" value={fmtMoney(ins.cpm)} />
        <MetricCell label="cpc" value={fmtMoney(ins.cpc)} />
      </div>
      <div className="flex items-center gap-1 w-[68px] justify-end" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => onStatusChange(camp.id, camp.status === "ACTIVE" ? "PAUSED" : "ACTIVE", camp.name)}
          className="p-1.5 rounded-lg border border-[#363b45] text-zinc-400 hover:text-gold hover:border-gold/30 transition-colors"
          data-testid={`toggle-${camp.id}`}>
          {camp.status === "ACTIVE" ? <Pause size={14} /> : <Play size={14} />}
        </button>
        {camp.daily_budget ? (
          <button onClick={() => onBudgetChange(camp.id, camp.daily_budget, camp.name)}
            className="p-1.5 rounded-lg border border-[#363b45] text-zinc-400 hover:text-gold hover:border-gold/30 transition-colors"
            data-testid={`budget-${camp.id}`}>
            <DollarSign size={14} />
          </button>
        ) : (
          <div className="w-[30px]" />
        )}
      </div>
      <ChevronRight size={16} className="text-zinc-600 group-hover:text-zinc-400 transition-colors" />
    </div>
  );
}

function AdSetRow({ adset, onDrill, onStatusChange }) {
  const ins = adset.insights || {};
  return (
    <div className="flex items-center gap-4 p-4 border-b border-[#222630] hover:bg-[#1a1d21] transition-colors cursor-pointer group"
      onClick={() => onDrill(adset.id)} data-testid={`adset-${adset.id}`}>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-zinc-200 font-medium truncate">{adset.name}</p>
        <p className="text-xs text-zinc-500 mt-0.5">{adset.targeting_summary}</p>
      </div>
      <StatusBadge status={adset.status} />
      <div className="hidden lg:grid grid-cols-4 gap-0" style={{ width: "352px" }}>
        <MetricCell label="spend" value={fmtMoney(ins.spend)} />
        <MetricCell label="impr" value={fmt(ins.impressions)} />
        <MetricCell label="clicks" value={fmt(ins.clicks)} />
        <MetricCell label="ctr" value={fmtPct(ins.ctr)} />
      </div>
      <div onClick={(e) => e.stopPropagation()}>
        <button onClick={() => onStatusChange(adset.id, adset.status === "ACTIVE" ? "PAUSED" : "ACTIVE", adset.name)}
          className="p-1.5 rounded-lg border border-[#363b45] text-zinc-400 hover:text-gold transition-colors">
          {adset.status === "ACTIVE" ? <Pause size={14} /> : <Play size={14} />}
        </button>
      </div>
      <ChevronRight size={16} className="text-zinc-600 group-hover:text-zinc-400" />
    </div>
  );
}

function AdCard({ ad, onStatusChange }) {
  const ins = ad.insights || {};
  const creative = ad.creative || {};
  const thumb = creative.thumbnail_url || creative.image_url || "";
  return (
    <div className="bg-studio-raised border border-[#2a2e36] rounded-xl overflow-hidden brand-card" data-testid={`ad-${ad.id}`}>
      {thumb && <div className="h-32 bg-studio-surface"><img src={thumb} alt="" className="w-full h-full object-cover" /></div>}
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm text-zinc-200 font-medium truncate flex-1">{ad.name}</p>
          <StatusBadge status={ad.status} />
        </div>
        {creative.body && <p className="text-xs text-zinc-400 line-clamp-2 mb-3">{creative.body}</p>}
        <div className="grid grid-cols-3 gap-2 text-center">
          <MetricCell label="spend" value={fmtMoney(ins.spend)} />
          <MetricCell label="clicks" value={fmt(ins.clicks)} />
          <MetricCell label="ctr" value={fmtPct(ins.ctr)} />
        </div>
        <button onClick={() => onStatusChange(ad.id, ad.status === "ACTIVE" ? "PAUSED" : "ACTIVE", ad.name)}
          className="mt-3 w-full flex items-center justify-center gap-1.5 h-8 rounded-lg border border-[#363b45] text-zinc-400 text-xs hover:text-gold transition-colors">
          {ad.status === "ACTIVE" ? <><Pause size={12} /> Pause</> : <><Play size={12} /> Resume</>}
        </button>
      </div>
    </div>
  );
}

export default function AdsManager() {
  const [accounts, setAccounts] = useState([]);
  const [activeAccount, setActiveAccount] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [adsets, setAdsets] = useState([]);
  const [ads, setAds] = useState([]);
  const [spendData, setSpendData] = useState([]);
  const [view, setView] = useState("campaigns"); // campaigns | adsets | ads | spend
  const [drillId, setDrillId] = useState(null);
  const [drillName, setDrillName] = useState("");
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [budgetEdit, setBudgetEdit] = useState(null);
  const [budgetValue, setBudgetValue] = useState("");
  const [campFilter, setCampFilter] = useState("all"); // all | campaigns | boosts
  const [analysisText, setAnalysisText] = useState("");
  const [analyzing, setAnalyzing] = useState(false);

  const fetchAccounts = useCallback(async () => {
    const res = await api.get("/ads/accounts");
    setAccounts(res.data.accounts);
    setActiveAccount("all");
  }, []);

  const fetchCampaigns = useCallback(async (accId) => {
    if (!accId) return;
    setLoading(true);
    try {
      if (accId === "all") {
        // Fetch from all accounts and merge
        const res = await api.get("/ads/accounts");
        const allCampaigns = [];
        for (const acc of res.data.accounts) {
          try {
            const cr = await api.get(`/ads/${acc.id}/campaigns`);
            for (const c of cr.data.campaigns) {
              c._account = acc;
            }
            allCampaigns.push(...cr.data.campaigns);
          } catch { /* skip failed account */ }
        }
        setCampaigns(allCampaigns);
      } else {
        const res = await api.get(`/ads/${accId}/campaigns`);
        setCampaigns(res.data.campaigns);
      }
    } catch { toast.error("Failed to load campaigns"); }
    setLoading(false);
  }, []);

  const fetchSpend = useCallback(async (accId) => {
    try {
      const res = await api.get(`/ads/${accId}/spend?days=30`);
      setSpendData(res.data.spend_data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);
  useEffect(() => {
    if (activeAccount) {
      fetchCampaigns(activeAccount);
      if (activeAccount !== "all") fetchSpend(activeAccount);
      else setSpendData([]);
      setView("campaigns");
    }
  }, [activeAccount, fetchCampaigns, fetchSpend]);

  const drillToCampaign = async (campId) => {
    setLoading(true);
    setDrillId(campId);
    setDrillName(campaigns.find((c) => c.id === campId)?.name || "");
    try {
      const res = await api.get(`/ads/campaigns/${campId}/adsets`);
      setAdsets(res.data.adsets);
      setView("adsets");
    } catch { toast.error("Failed to load ad sets"); }
    setLoading(false);
  };

  const drillToAdSet = async (adsetId) => {
    setLoading(true);
    try {
      const res = await api.get(`/ads/adsets/${adsetId}/ads`);
      setAds(res.data.ads);
      setView("ads");
    } catch { toast.error("Failed to load ads"); }
    setLoading(false);
  };

  const handleStatusChange = (entityId, newStatus, name) => {
    setConfirm({ entityId, newStatus, name, type: "status" });
  };

  const confirmAction = async () => {
    if (!confirm) return;
    setActionLoading(true);
    try {
      if (confirm.type === "status") {
        if (confirm.batchIds) {
          // Batch pause/resume
          for (const id of confirm.batchIds) {
            await api.post(`/ads/${id}/status`, { status: confirm.newStatus });
          }
          toast.success(`${confirm.name} ${confirm.newStatus === "ACTIVE" ? "resumed" : "paused"}`);
        } else {
          await api.post(`/ads/${confirm.entityId}/status`, { status: confirm.newStatus });
          toast.success(`${confirm.name} ${confirm.newStatus === "ACTIVE" ? "resumed" : "paused"}`);
        }      } else if (confirm.type === "budget") {
        await api.post(`/ads/${confirm.entityId}/budget`, { daily_budget: parseInt(budgetValue) * 100 });
        toast.success(`Budget updated for ${confirm.name}`);
      }
      fetchCampaigns(activeAccount);
    } catch (e) { toast.error("Action failed"); }
    setActionLoading(false);
    setConfirm(null);
    setBudgetEdit(null);
  }; 

  const handleBudgetChange = (entityId, currentBudget, name) => {
    setBudgetValue(String(Math.round(Number(currentBudget) / 100)));
    setConfirm({ entityId, name, type: "budget" });
  };

  const goBack = () => {
    if (view === "ads") { setView("adsets"); setAds([]); }
    else if (view === "adsets") { setView("campaigns"); setAdsets([]); setDrillId(null); }
    else if (view === "spend" || view === "analysis") { setView("campaigns"); }
  };

  const handleAnalyze = async () => {
    if (activeAccount === "all") {
      toast.error("Select a specific account to analyze");
      return;
    }
    setAnalyzing(true);
    setView("analysis");
    setAnalysisText("");
    try {
      const res = await api.post(`/ads/${activeAccount}/analyze`);
      setAnalysisText(res.data.analysis);
      toast.success(`Analyzed ${res.data.campaigns_analyzed} campaigns`);
    } catch (e) {
      toast.error("Analysis failed");
      setAnalysisText("Analysis failed. Try again.");
    }
    setAnalyzing(false);
  };

  const handleAnalyzeCreatives = async () => {
    if (activeAccount === "all") {
      toast.error("Select a specific account first");
      return;
    }
    setAnalyzing(true);
    try {
      const res = await api.post(`/ads/${activeAccount}/analyze-creatives`);
      setAnalysisText((prev) => prev + "\n\n--- CREATIVE ANALYSIS ---\n\n" + res.data.analysis);
    } catch { toast.error("Creative analysis failed"); }
    setAnalyzing(false);
  };

  const handleAdStrategy = async () => {
    if (activeAccount === "all") {
      toast.error("Select a specific account first");
      return;
    }
    setAnalyzing(true);
    try {
      const res = await api.post(`/ads/${activeAccount}/generate-ad-strategy`);
      setAnalysisText((prev) => prev + "\n\n--- AD STRATEGY ---\n\n" + res.data.analysis);
      toast.success(`Strategy generated from ${res.data.ads_analyzed} ads`);
    } catch { toast.error("Strategy generation failed"); }
    setAnalyzing(false);
  };

  const activeAccName = activeAccount === "all" ? "All Accounts" : (accounts.find((a) => a.id === activeAccount)?.name || "");
  const tooltipStyle = { contentStyle: { background: "#1a1d21", border: "1px solid #2a2e36", borderRadius: "12px", fontSize: "12px", color: "#d4d4d8" } };

  const isBoostedPost = (c) => /^instagram post/i.test(c.name) || /^post:/i.test(c.name);
  const isLive = (c) => {
    const eff = c.effective_status || c.status;
    if (eff === "PAUSED") return false;
    // CBO campaigns (no direct budget) — trust effective_status
    if (!c.daily_budget && !c.lifetime_budget) return eff === "ACTIVE";
    // Campaigns with their own budget — check remaining
    const remaining = Number(c.budget_remaining ?? 9999);
    return remaining >= 100;
  };
  const filteredCampaigns = campFilter === "all" ? campaigns
    : campFilter === "campaigns" ? campaigns.filter((c) => !isBoostedPost(c))
    : campFilter === "boosts" ? campaigns.filter((c) => isBoostedPost(c))
    : campFilter === "live" ? campaigns.filter(isLive)
    : campaigns;
  const boostCount = campaigns.filter(isBoostedPost).length;
  const realCount = campaigns.length - boostCount;
  const liveCount = campaigns.filter(isLive).length;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="ads-manager">
      <div>
        <h1 className="font-display text-3xl font-bold text-zinc-100">Ads Manager</h1>
        <p className="section-label mt-2">// meta ad accounts</p>
      </div>

      {/* Account tabs */}
      <div className="flex items-center gap-2">
        <button onClick={() => setActiveAccount("all")}
          className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${activeAccount === "all"
            ? "text-gold border border-gold/30 bg-gold/5" : "text-zinc-400 border border-[#2a2e36] hover:border-[#363b45]"}`}
          data-testid="ads-tab-all">
          All
        </button>
        {accounts.map((acc) => (
          <button key={acc.id} onClick={() => setActiveAccount(acc.id)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${activeAccount === acc.id
              ? "text-gold border border-gold/30 bg-gold/5" : "text-zinc-400 border border-[#2a2e36] hover:border-[#363b45]"}`}
            data-testid={`ads-tab-${acc.id}`}>
            <span className="w-2 h-2 rounded-full inline-block mr-2" style={{ background: acc.color }} />
            {acc.name}
          </button>
        ))}
        <button onClick={() => setView(view === "spend" ? "campaigns" : "spend")}
          className={`ml-auto px-4 py-2 rounded-xl text-sm transition-all ${view === "spend"
            ? "text-gold border border-gold/30 bg-gold/5" : "text-zinc-400 border border-[#2a2e36]"}`}
          data-testid="ads-spend-tab">
          <DollarSign size={14} className="inline mr-1" /> {view === "spend" ? "Back" : "Spend"}
        </button>
        <button onClick={handleAnalyze} disabled={analyzing || activeAccount === "all"}
          className={`px-4 py-2 rounded-xl text-sm transition-all ${view === "analysis"
            ? "text-gold border border-gold/30 bg-gold/5" : "text-zinc-400 border border-[#2a2e36]"} disabled:opacity-40`}
          data-testid="ads-analyze-btn">
          <BarChart3 size={14} className="inline mr-1" /> {analyzing ? "Analyzing..." : "Analyze"}
        </button>
      </div>

      {/* Breadcrumb */}
      {view !== "campaigns" && view !== "spend" && view !== "analysis" && (
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <button onClick={goBack} className="flex items-center gap-1 text-gold hover:text-gold-bright transition-colors" data-testid="ads-back">
            <ArrowLeft size={14} /> Back
          </button>
          <span>/</span>
          <span className="text-zinc-300">{drillName || "..."}</span>
          {view === "ads" && <><span>/</span><span className="text-zinc-400">Ads</span></>}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-40"><div className="spinner-gold" /></div>
      ) : view === "spend" ? (
        <div className="bg-studio-raised border border-[#2a2e36] rounded-xl p-6">
          <h3 className="text-sm font-semibold text-zinc-200 mb-1">Daily Spend - {activeAccName}</h3>
          <p className="section-label mb-4">// last 30 days</p>
          {spendData.length === 0 ? (
            <p className="text-zinc-500 text-sm text-center py-8">No spend data available</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={spendData}>
                <XAxis dataKey="date_start" tick={{ fill: "#71717a", fontSize: 10, fontFamily: "Azeret Mono" }} axisLine={{ stroke: "#222630" }} tickLine={false}
                  tickFormatter={(v) => new Date(v).toLocaleDateString("en-US", { month: "short", day: "numeric" })} />
                <YAxis tick={{ fill: "#71717a", fontSize: 10, fontFamily: "Azeret Mono" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} />
                <Tooltip {...tooltipStyle} />
                <Bar dataKey="spend" fill="#c6a96f" radius={[4, 4, 0, 0]} name="Spend ($)" />
                <Line dataKey="clicks" stroke="#5ba89d" dot={false} name="Clicks" yAxisId="right" />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      ) : view === "analysis" ? (
        <div className="space-y-4">
          <div className="bg-studio-raised border border-[#2a2e36] rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-zinc-200">Performance Analysis</h3>
                <p className="section-label mt-1">// {activeAccName}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={handleAnalyzeCreatives} disabled={analyzing}
                  className="px-3 py-1.5 rounded-lg border border-[#363b45] text-zinc-400 text-xs hover:text-gold hover:border-gold/30 transition-colors disabled:opacity-40"
                  data-testid="analyze-creatives-btn">
                  {analyzing ? <Loader2 size={12} className="inline animate-spin mr-1" /> : null}
                  Analyze Creatives
                </button>
                <button onClick={handleAdStrategy} disabled={analyzing}
                  className="px-3 py-1.5 rounded-lg border border-gold/30 text-gold text-xs hover:bg-gold/5 transition-colors disabled:opacity-40"
                  data-testid="ad-strategy-btn">
                  {analyzing ? <Loader2 size={12} className="inline animate-spin mr-1" /> : null}
                  Generate Ad Strategy
                </button>
                <button onClick={goBack}
                  className="px-3 py-1.5 rounded-lg border border-[#363b45] text-zinc-400 text-xs hover:text-gold transition-colors">
                  Back to Campaigns
                </button>
              </div>
            </div>
            {analyzing && !analysisText ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={24} className="animate-spin text-gold mr-3" />
                <span className="text-zinc-400 text-sm">Analyzing campaign performance with Gemini...</span>
              </div>
            ) : analysisText ? (
              <div className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">{analysisText}</div>
            ) : (
              <p className="text-zinc-500 text-sm text-center py-8">No analysis yet. Click Analyze to start.</p>
            )}
          </div>
        </div>
      ) : view === "campaigns" ? (
        <div className="bg-studio-raised border border-[#2a2e36] rounded-xl overflow-hidden">
          <div className="p-4 border-b border-[#222630] flex items-center gap-3">
            <span className="section-label">// campaigns -- {activeAccName}</span>
            <div className="flex items-center bg-studio-surface border border-[#2a2e36] rounded-xl overflow-hidden ml-auto">
              <button onClick={() => setCampFilter("all")}
                className={`px-3 py-1.5 text-xs transition-colors ${campFilter === "all" ? "bg-gold/10 text-gold" : "text-zinc-500 hover:text-zinc-300"}`}
                data-testid="camp-filter-all">
                All <span className="font-mono ml-1 text-zinc-600">{campaigns.length}</span>
              </button>
              <button onClick={() => setCampFilter("live")}
                className={`px-3 py-1.5 text-xs transition-colors ${campFilter === "live" ? "bg-status-green/10 text-status-green" : "text-zinc-500 hover:text-zinc-300"}`}
                data-testid="camp-filter-live">
                Live <span className="font-mono ml-1 text-zinc-600">{liveCount}</span>
              </button>
              <button onClick={() => setCampFilter("campaigns")}
                className={`px-3 py-1.5 text-xs transition-colors ${campFilter === "campaigns" ? "bg-gold/10 text-gold" : "text-zinc-500 hover:text-zinc-300"}`}
                data-testid="camp-filter-campaigns">
                Campaigns <span className="font-mono ml-1 text-zinc-600">{realCount}</span>
              </button>
              <button onClick={() => setCampFilter("boosts")}
                className={`px-3 py-1.5 text-xs transition-colors ${campFilter === "boosts" ? "bg-gold/10 text-gold" : "text-zinc-500 hover:text-zinc-300"}`}
                data-testid="camp-filter-boosts">
                Boosts <span className="font-mono ml-1 text-zinc-600">{boostCount}</span>
              </button>
            </div>
          </div>
          {filteredCampaigns.length === 0 ? (
            <p className="text-zinc-500 text-sm text-center py-8">No campaigns found</p>
          ) : (
            filteredCampaigns.map((c) => (
              <CampaignRow key={c.id} camp={c} onDrill={drillToCampaign}
                onStatusChange={handleStatusChange} onBudgetChange={handleBudgetChange} />
            ))
          )}
        </div>
      ) : view === "adsets" ? (
        <div className="bg-studio-raised border border-[#2a2e36] rounded-xl overflow-hidden">
          <div className="p-4 border-b border-[#222630]">
            <span className="section-label">// ad sets</span>
          </div>
          {adsets.length === 0 ? (
            <p className="text-zinc-500 text-sm text-center py-8">No ad sets found</p>
          ) : (
            adsets.map((a) => <AdSetRow key={a.id} adset={a} onDrill={drillToAdSet} onStatusChange={handleStatusChange} />)
          )}
        </div>
      ) : view === "ads" ? (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <p className="section-label">// ad creatives</p>
            <div className="flex gap-2 ml-auto">
              <button onClick={() => {
                const activeAds = ads.filter(a => a.status === "ACTIVE");
                if (activeAds.length === 0) return;
                setConfirm({ entityId: "BATCH", newStatus: "PAUSED", name: `${activeAds.length} active ads`, type: "status", batchIds: activeAds.map(a => a.id) });
              }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#363b45] text-zinc-400 text-xs hover:text-gold hover:border-gold/30 transition-colors"
                data-testid="pause-all-ads">
                <Pause size={12} /> Pause All
              </button>
              <button onClick={() => {
                const pausedAds = ads.filter(a => a.status === "PAUSED");
                if (pausedAds.length === 0) return;
                setConfirm({ entityId: "BATCH", newStatus: "ACTIVE", name: `${pausedAds.length} paused ads`, type: "status", batchIds: pausedAds.map(a => a.id) });
              }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#363b45] text-zinc-400 text-xs hover:text-gold hover:border-gold/30 transition-colors"
                data-testid="resume-all-ads">
                <Play size={12} /> Resume All
              </button>
            </div>
          </div>
          {ads.length === 0 ? (
            <p className="text-zinc-500 text-sm text-center py-8">No ads found</p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {ads.map((a) => <AdCard key={a.id} ad={a} onStatusChange={handleStatusChange} />)}
              </div>
              {ads.filter((a) => a.insights?.ctr).length > 1 && (
                <div className="mt-6 bg-studio-raised border border-[#2a2e36] rounded-xl p-6">
                  <h3 className="text-sm font-semibold text-zinc-200 mb-1">Creative Performance Ranking</h3>
                  <p className="section-label mb-4">// sorted by ctr</p>
                  <div className="space-y-2">
                    {[...ads].filter((a) => a.insights?.ctr).sort((a, b) => Number(b.insights.ctr) - Number(a.insights.ctr)).map((a, i) => (
                      <div key={a.id} className="flex items-center gap-4 p-3 rounded-lg bg-studio-surface">
                        <span className={`stat-number text-lg ${i === 0 ? "text-gold" : "text-zinc-400"}`}>{i + 1}</span>
                        <span className="text-sm text-zinc-200 flex-1 truncate">{a.name}</span>
                        <MetricCell label="ctr" value={fmtPct(a.insights.ctr)} />
                        <MetricCell label="cpc" value={fmtMoney(a.insights.cpc)} />
                        <MetricCell label="spend" value={fmtMoney(a.insights.spend)} />
                        {i === 0 && <span className="status-badge status-published">winner</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      ) : null}

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={!!confirm}
        onClose={() => { setConfirm(null); setBudgetEdit(null); }}
        onConfirm={confirmAction}
        loading={actionLoading}
        title={confirm?.type === "budget" ? "Change Budget" : `${confirm?.newStatus === "ACTIVE" ? "Resume" : "Pause"} Campaign`}
        description={
          confirm?.type === "budget" ? (
            <div className="space-y-3">
              <p>Update daily budget for <strong className="text-zinc-200">{confirm?.name}</strong></p>
              <div className="flex items-center gap-2">
                <span className="text-zinc-400 text-sm">$</span>
                <input value={budgetValue} onChange={(e) => setBudgetValue(e.target.value)} type="number"
                  className="w-32 h-10 px-3 bg-studio-surface border border-[#2a2e36] rounded-xl text-zinc-200 text-sm outline-none focus:border-gold-dim"
                  data-testid="budget-input" />
                <span className="text-zinc-500 text-xs">/ day</span>
              </div>
            </div>
          ) : `Are you sure you want to ${confirm?.newStatus === "ACTIVE" ? "resume" : "pause"} "${confirm?.name}"?`
        }
      />
    </div>
  );
}
